import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError, uploadFile } from "@/lib/api";
import Layout, { PageTitle } from "@/components/Layout";
import { NAV_GURU } from "@/lib/nav";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Eye, X } from "lucide-react";
import { FilterBar } from "@/components/FilterBar";
import PdfViewerModal from "@/components/PdfViewerModal";
import { MAPEL_AKADEMIK, PROGRAM_LABEL, SEMESTER_OPTIONS, TAHUN_AJARAN_OPTIONS } from "./_shared";

const F_EMPTY = { guru: "", program: "", tahun_ajaran: "", semester: "", mapel: "" };

export function ModulAjarGuru() {
  const { user } = useAuth();
  const [all, setAll] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ tahun_ajaran: "2025/2026", semester: "I", program: "intra_kurikuler", mata_pelajaran: MAPEL_AKADEMIK[0], file_id: null });
  const [uploading, setUploading] = useState(false);
  const [filters, setFilters] = useState(F_EMPTY);
  const [applied, setApplied] = useState(F_EMPTY);
  const [viewer, setViewer] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/modul-ajar"); setAll(data); }
    catch (e) { console.error(e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const items = all.filter((m) => {
    if (applied.guru && !(m.guru_nama || "").toLowerCase().includes(applied.guru.toLowerCase())) return false;
    if (applied.program && m.program !== applied.program) return false;
    if (applied.tahun_ajaran && m.tahun_ajaran !== applied.tahun_ajaran) return false;
    if (applied.semester && m.semester !== applied.semester) return false;
    if (applied.mapel && !m.mata_pelajaran.toLowerCase().includes(applied.mapel.toLowerCase())) return false;
    return true;
  });

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try { const r = await uploadFile(f); setForm({ ...form, file_id: r.id }); toast.success("Modul diunggah"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!form.file_id) return toast.error("Upload file modul dulu");
    try { await api.post("/modul-ajar", form); toast.success("Modul tersimpan"); setShow(false); setForm({ ...form, file_id: null }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Hapus modul?")) return;
    try { await api.delete(`/modul-ajar/${id}`); toast.success("Dihapus"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Gagal hapus"); }
  };

  return (
    <Layout nav={NAV_GURU}>
      <PageTitle title="Modul Ajar (Semua Guru)" action={
        <button onClick={() => setShow(true)} className="pp-btn-primary" data-testid="open-modul"><Plus className="w-4 h-4" /> Upload Modul Saya</button>
      } />

      <FilterBar
        filters={filters} setFilters={setFilters}
        onApply={() => setApplied({ ...filters })}
        onReset={() => { setFilters(F_EMPTY); setApplied(F_EMPTY); }}
        dataTestidPrefix="g-modul-filter"
        fields={[
          { key: "guru", type: "text", label: "Guru", placeholder: "Cari guru" },
          { key: "program", type: "select", label: "Program", options: [{ v: "ko_kurikuler", label: "Ko-Kurikuler" }, { v: "intra_kurikuler", label: "Intra-Kurikuler" }, { v: "ekstra_kurikuler", label: "Ekstra-Kurikuler" }] },
          { key: "tahun_ajaran", type: "select", label: "TA", options: TAHUN_AJARAN_OPTIONS },
          { key: "semester", type: "select", label: "Sem", options: [{ v: "I", label: "I" }, { v: "II", label: "II" }] },
          { key: "mapel", type: "text", label: "Mapel", placeholder: "Cari mapel" },
        ]}
      />

      {show && (
        <section className="pp-card p-6 mb-6 animate-fade-up">
          <div className="flex items-center justify-between mb-5"><h3 className="text-lg font-semibold">Upload Modul Ajar</h3><button onClick={() => setShow(false)}><X className="w-5 h-5 text-slate-400" /></button></div>
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
            <div><label className="pp-label">Tahun Ajaran</label>
              <select className="pp-input" value={form.tahun_ajaran} onChange={(e) => setForm({ ...form, tahun_ajaran: e.target.value })}>
                {TAHUN_AJARAN_OPTIONS.map((t) => <option key={t}>{t}</option>)}
              </select></div>
            <div><label className="pp-label">Semester</label>
              <select className="pp-input" value={form.semester} onChange={(e) => setForm({ ...form, semester: e.target.value })}>
                {SEMESTER_OPTIONS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
              </select></div>
            <div><label className="pp-label">Program</label>
              <select className="pp-input" value={form.program} onChange={(e) => setForm({ ...form, program: e.target.value })}>
                <option value="ko_kurikuler">Ko-Kurikuler</option>
                <option value="intra_kurikuler">Intra-Kurikuler</option>
                <option value="ekstra_kurikuler">Ekstra-Kurikuler</option>
              </select></div>
            <div><label className="pp-label">Mata Pelajaran</label>
              <input required className="pp-input" value={form.mata_pelajaran} onChange={(e) => setForm({ ...form, mata_pelajaran: e.target.value })} /></div>
            <div className="sm:col-span-2"><label className="pp-label">File Modul (PDF) *</label>
              <label className="pp-btn-secondary cursor-pointer"><Upload className="w-4 h-4" />{uploading ? "Mengunggah…" : (form.file_id ? "Ganti File" : "Pilih PDF")}
                <input type="file" accept=".pdf" onChange={upload} className="hidden" /></label>
              {form.file_id && <span className="ml-3 text-sm text-emerald-600">✓ File siap</span>}
            </div>
            <div className="sm:col-span-2 flex justify-end gap-2">
              <button type="button" onClick={() => setShow(false)} className="pp-btn-secondary">Batal</button>
              <button type="submit" className="pp-btn-primary" data-testid="submit-modul">Simpan Modul</button>
            </div>
          </form>
        </section>
      )}

      <section className="pp-card pp-freeze overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="pp-table-header">
            <th className="text-left px-5 py-3">Guru</th>
            <th className="text-left px-5 py-3">Periode</th>
            <th className="text-left px-5 py-3">Program</th>
            <th className="text-left px-5 py-3">Mata Pelajaran</th>
            <th className="text-left px-5 py-3">File</th>
            <th className="text-right px-5 py-3">Aksi</th>
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-slate-400 italic">Belum ada modul ajar.</td></tr>}
            {items.map((m) => (
              <tr key={m.id} className="pp-row">
                <td className="px-5 py-3 font-medium">{m.guru_nama || "—"}</td>
                <td className="px-5 py-3">{m.tahun_ajaran} · Sem {m.semester}</td>
                <td className="px-5 py-3"><span className="pp-badge-info">{PROGRAM_LABEL[m.program]}</span></td>
                <td className="px-5 py-3 font-medium">{m.mata_pelajaran}</td>
                <td className="px-5 py-3">
                  <button onClick={() => setViewer({ id: m.file_id, title: `${m.mata_pelajaran} · ${m.guru_nama || "—"}` })} className="pp-btn-ghost text-xs" data-testid={`view-modul-${m.id}`}>
                    <Eye className="w-3.5 h-3.5" /> Lihat
                  </button>
                </td>
                <td className="px-5 py-3 text-right">
                  {m.guru_id === user?.linked_id
                    ? <button onClick={() => del(m.id)} className="pp-btn-danger"><Trash2 className="w-3.5 h-3.5" />Hapus</button>
                    : <span className="text-xs text-slate-400 italic">milik guru lain</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {viewer && <PdfViewerModal fileId={viewer.id} title={viewer.title} onClose={() => setViewer(null)} />}
    </Layout>
  );
}
